// One-way migration: the legacy flat OrderFormCollapsibleConfig.entryCondition*
// fields (price / schedule / action_event + one-level ALL/ANY group JSON) ->
// the recursive ConditionNode model. Used when a saved config predates
// entryConditionTreeJson, so existing trigger configs still open. Once the new
// editor writes the tree JSON, that value is authoritative.
import type { GroupConditionItem, OrderFormCollapsibleConfig } from '@/state/order'

import {
  type ActionEventTargetTypeToken,
  type ActionEventTypeToken,
  type ConditionNode,
  newNodeId,
  type OperatorToken,
  type PriceSourceToken,
  type ScheduleVariantToken,
} from './trigger-condition-model'

/** Tag a migrated node with a stable client id so the editor's React keys don't
 * fall back to array index (which mis-associates rows on add/remove). */
function withId(node: ConditionNode): ConditionNode {
  return { ...node, id: newNodeId() }
}

function scheduleNode(variant: ScheduleVariantToken, rawValue: string): ConditionNode {
  if (variant === 'fire_at') {
    // The model stores an absolute ms epoch string. Legacy values may be either
    // a ms-epoch string already or a timezone-naive datetime-local string.
    if (/^\d+$/.test(rawValue)) {
      return withId({ kind: 'schedule', variant: 'fire_at', value: rawValue })
    }
    const ms = rawValue ? new Date(rawValue).getTime() : NaN
    return withId({
      kind: 'schedule',
      variant: 'fire_at',
      value: Number.isFinite(ms) ? String(ms) : '',
    })
  }
  return withId({ kind: 'schedule', variant: 'delay', value: rawValue })
}

function legacyGroupChildToNode(item: GroupConditionItem): ConditionNode | null {
  switch (item.type) {
    case 'price':
      return withId({
        kind: 'price',
        symbol: item.instrument,
        source: item.source as PriceSourceToken,
        op: item.operator as OperatorToken,
        value: item.value,
      })
    case 'schedule':
      return scheduleNode(item.variant, item.value)
    case 'action_event':
      return withId({
        kind: 'action_event',
        eventType: item.eventType as ActionEventTypeToken,
        targetType: item.targetType as ActionEventTargetTypeToken,
        targetValue: item.targetValue,
        partialFillThreshold: item.partialFillThreshold,
      })
    default:
      return null
  }
}

/** Build a ConditionNode tree from legacy flat config, or null if nothing is set. */
export function legacyConfigToTree(config: OrderFormCollapsibleConfig): ConditionNode | null {
  const logic = config.entryConditionLogic || 'single'

  if (logic === 'all' || logic === 'any') {
    let items: GroupConditionItem[] = []
    try {
      items = JSON.parse(config.entryConditionGroupJson || '[]')
    } catch {
      items = []
    }
    const children = items.map(legacyGroupChildToNode).filter((n): n is ConditionNode => n !== null)
    if (children.length === 0) return null
    return withId({ kind: logic === 'all' ? 'and' : 'or', children })
  }

  const triggerType = config.entryConditionTriggerType || 'price'

  if (triggerType === 'schedule') {
    const variant = (config.entryConditionScheduleVariant || 'delay') as ScheduleVariantToken
    const rawValue =
      variant === 'delay'
        ? config.entryConditionDelaySeconds || ''
        : config.entryConditionFireAtMs || ''
    if (!rawValue) return null
    return scheduleNode(variant, rawValue)
  }

  if (triggerType === 'action_event') {
    const targetValue = config.entryConditionActionTargetValue || ''
    if (!targetValue) return null
    return withId({
      kind: 'action_event',
      eventType: (config.entryConditionActionEventType || 'filled') as ActionEventTypeToken,
      targetType: (config.entryConditionActionTargetType ||
        'order_id') as ActionEventTargetTypeToken,
      targetValue,
      partialFillThreshold: config.entryConditionActionPartialFillThreshold || '',
    })
  }

  // price (default)
  const value = config.entryConditionPrice || ''
  const symbol = config.entryConditionInstrument || ''
  if (!value && !symbol) return null
  return withId({
    kind: 'price',
    symbol,
    source: (config.entryConditionPriceRef || 'mark') as PriceSourceToken,
    op: (config.entryConditionOperator || 'equal_to') as OperatorToken,
    value,
  })
}
